/**
 * Editor reutilizable de componentes de combo (BOM).
 * Buscar + agregar (misma lógica que CatalogProductComboPanel).
 */
import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing, ticketColors, ticketFonts } from '../../shared/theme/public-api';
import { typography } from '../../shared/theme/typography';
import { colors } from '../../shared/theme/colors';
import { matchesSearchText } from '../../shared/format/searchText';
import { MIN_PRODUCT_SEARCH_QUERY_CHARS } from '../../shared/product-search/productSearchUx';
import type { ComboDraftComponent } from '../domain/productCombos';

export type ComboComponentPickOption = {
  id: string;
  name: string;
  sku?: string | null;
  barcode?: string | null;
};

type Props = {
  components: ComboDraftComponent[];
  productOptions: ComboComponentPickOption[];
  comboProductId?: string | null;
  onChange: (next: ComboDraftComponent[]) => void;
  disabled?: boolean;
};

function productMatchesQuery(p: ComboComponentPickOption, q: string): boolean {
  if (!q) return true;
  if (matchesSearchText(p.name, q)) return true;
  const sku = p.sku?.trim();
  const barcode = p.barcode?.trim();
  if (sku && matchesSearchText(sku, q)) return true;
  if (barcode && matchesSearchText(barcode, q)) return true;
  return false;
}

export function ComboComponentsEditor({
  components,
  productOptions,
  comboProductId,
  onChange,
  disabled = false,
}: Props) {
  const [addFilter, setAddFilter] = useState('');

  const productNameCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of productOptions) counts.set(p.name, (counts.get(p.name) ?? 0) + 1);
    return counts;
  }, [productOptions]);

  const productLabel = useCallback(
    (p: ComboComponentPickOption): string => {
      if ((productNameCounts.get(p.name) ?? 0) <= 1) return p.name;
      const code = p.sku?.trim() || p.barcode?.trim();
      if (code) return `${p.name} · ${code}`;
      return `${p.name} · #${p.id.slice(-4)}`;
    },
    [productNameCounts],
  );

  const resolveName = useCallback(
    (id: string) => {
      const p = productOptions.find((prod) => prod.id === id);
      return p ? productLabel(p) : 'Producto';
    },
    [productOptions, productLabel],
  );

  const usedIds = useMemo(() => new Set(components.map((c) => c.productId)), [components]);

  const searchQuery = addFilter.trim();
  const searchReady = searchQuery.length >= MIN_PRODUCT_SEARCH_QUERY_CHARS;

  const candidates = useMemo(() => {
    if (!searchReady) return [];
    return productOptions.filter(
      (p) =>
        p.id !== comboProductId &&
        !usedIds.has(p.id) &&
        productMatchesQuery(p, searchQuery),
    );
  }, [productOptions, comboProductId, usedIds, searchQuery, searchReady]);

  const addComponent = (productId: string) => {
    if (disabled || usedIds.has(productId)) return;
    onChange([...components, { productId, qty: 1 }]);
    setAddFilter('');
  };

  const updateRow = (index: number, patch: Partial<ComboDraftComponent>) => {
    onChange(components.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const removeRow = (index: number) => {
    onChange(components.filter((_, i) => i !== index));
  };

  if (!disabled && productOptions.length === 0) {
    return (
      <Text style={styles.muted}>
        No hay productos para armar partes. Creá productos simples en el catálogo primero.
      </Text>
    );
  }

  return (
    <View style={styles.wrap}>
      {components.length === 0 ? (
        <Text style={styles.muted}>Todavía no hay partes. Buscá abajo y tocá + para agregar.</Text>
      ) : null}

      {components.map((row, index) => (
        <View key={`${row.productId}-${index}`} style={styles.row}>
          <TextInput
            style={styles.qty}
            keyboardType="decimal-pad"
            editable={!disabled}
            value={String(row.qty)}
            onChangeText={(t) => {
              const n = Number(t.replace(',', '.'));
              updateRow(index, { qty: Number.isFinite(n) ? n : row.qty });
            }}
            accessibilityLabel="Cantidad"
            testID={`combo-qty-${row.productId}`}
          />
          <Text style={styles.name} numberOfLines={2}>
            × {resolveName(row.productId)}
          </Text>
          {!disabled ? (
            <TouchableOpacity
              accessibilityLabel="Quitar componente"
              onPress={() => removeRow(index)}
              style={styles.removeBtn}
              testID={`combo-remove-${row.productId}`}
            >
              <Ionicons name="trash-outline" size={18} color={ticketColors.bad} />
            </TouchableOpacity>
          ) : null}
        </View>
      ))}

      {!disabled ? (
        <>
          <TextInput
            style={styles.search}
            placeholder="Buscar producto para agregar…"
            placeholderTextColor={colors.textMuted}
            value={addFilter}
            onChangeText={setAddFilter}
            testID="combo-component-search"
          />
          {!searchReady ? (
            <Text style={styles.muted}>
              Escribí al menos {MIN_PRODUCT_SEARCH_QUERY_CHARS} caracteres para ver productos.
            </Text>
          ) : candidates.length === 0 ? (
            <Text style={styles.muted}>
              {usedIds.size >= productOptions.length - (comboProductId ? 1 : 0)
                ? 'Ya agregaste todos los productos disponibles.'
                : 'Nada coincide. Probá otro nombre o código.'}
            </Text>
          ) : (
            <View style={styles.pickList}>
              {candidates.slice(0, 40).map((p) => (
                <TouchableOpacity
                  key={p.id}
                  style={styles.pickRow}
                  onPress={() => addComponent(p.id)}
                  testID={`combo-add-${p.id}`}
                >
                  <Text style={styles.pickName} numberOfLines={2}>
                    {productLabel(p)}
                  </Text>
                  <Ionicons name="add-circle-outline" size={22} color={colors.accent} />
                </TouchableOpacity>
              ))}
              {candidates.length > 40 ? (
                <Text style={styles.muted}>Mostrando 40. Acotá la búsqueda.</Text>
              ) : null}
            </View>
          )}
        </>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: spacing.sm },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  name: {
    flex: 1,
    ...typography.body,
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
  },
  qty: {
    width: 56,
    borderWidth: 1,
    borderColor: ticketColors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.xs,
    ...typography.body,
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
    textAlign: 'center',
  },
  removeBtn: { padding: spacing.xs },
  search: {
    marginTop: spacing.xs,
    borderWidth: 1,
    borderColor: ticketColors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    ...typography.body,
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
  },
  pickList: { gap: 2 },
  pickRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.border,
  },
  pickName: {
    flex: 1,
    ...typography.body,
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
  },
  muted: {
    ...typography.body,
    fontFamily: ticketFonts.sans,
    color: colors.textMuted,
  },
});
